import * as React from 'react';
import { useState } from 'react';
import classnames from '../utils/classnames.tsx';
import { withIcon } from './Icon.tsx';
import { faArrowDownShortWide, faArrowUpWideShort, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import DropdownMenu from './DropdownMenu.tsx';
import Button from './Button.tsx';

const AscendingIcon = withIcon(faArrowDownShortWide);
const DescendingIcon = withIcon(faArrowUpWideShort);
const ChevronDownIcon = withIcon(faChevronDown);

interface OrderByDropdownOption {
    value: string;
    label: string;
    ascLabel?: string;
    descLabel?: string;
    isDate?: boolean;
}

interface OrderByDropdownProps {
    label?: string;
    options?: OrderByDropdownOption[];
    selected?: string;
    defaultValue?: string;
    onSelectedChange?: (selected: string) => void;
    className?: string;
    inline?: boolean;
}

const isDescending = (value: string) => value.startsWith('-');
const removeDirection = (value: string) => value.replace(/^-/, '');

const OrderByDropdown = ({
    label: defaultLabel,
    options,
    selected,
    defaultValue,
    onSelectedChange,
    className,
    inline = false,
}: OrderByDropdownProps) => {
    const { t } = useTranslation('assistance');

    if (!selected) {
        selected = '';
    }

    const direction: 'asc' | 'desc' = isDescending(selected || defaultValue) ? 'desc' : 'asc';

    const selectedOption = options?.find((option) => option.value === removeDirection(selected || defaultValue));

    const [isOpen, setIsOpen] = useState(false);

    const handleDirectionChange = (direction: 'asc' | 'desc') => {
        const newSelected = direction === 'asc' ? selectedOption?.value : `-${selectedOption?.value}`;
        onSelectedChange(newSelected);
    };

    const handleValueChange = (value: string) => {
        onSelectedChange(value);
    };

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <div className={classnames('relative text-sm', className)}>
                <DropdownMenu.Trigger asChild>
                    <Button className={classnames('pr-8 flex gap-2 items-center', inline && 'border-none shadow-none')}>
                        {direction === 'asc' ? <AscendingIcon /> : <DescendingIcon />}
                        {selectedOption?.label || defaultLabel}
                    </Button>
                </DropdownMenu.Trigger>

                <ChevronDownIcon
                    className={classnames(
                        'absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none',
                        isOpen && 'text-content-brand-strong'
                    )}
                />
            </div>
            <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" className="p-0">
                    <div className="flex flex-col">
                        <div className="flex p-1 gap-1 border-b border-line-weak border-solid">
                            <button
                                className={classnames(
                                    'text-sm text-content-strong p-1 rounded px-3 py-1.5 flex gap-2 items-center justify-center w-40 hover:bg-surface-strong transition-colors',
                                    direction === 'asc' &&
                                        '!text-content-brand-strong !bg-surface-brand-medium-default hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active'
                                )}
                                onClick={() => handleDirectionChange('asc')}
                            >
                                <AscendingIcon />{' '}
                                {selectedOption?.ascLabel
                                    ? selectedOption?.ascLabel
                                    : selectedOption?.isDate
                                      ? t('overview.filters.orderBy.options.ascendingDate')
                                      : t('overview.filters.orderBy.options.ascending')}
                            </button>
                            <button
                                className={classnames(
                                    'text-sm text-content-strong p-1 rounded px-3 py-1.5 flex gap-2 items-center justify-center w-40 hover:bg-surface-strong transition-colors',
                                    direction === 'desc' &&
                                        '!text-content-brand-strong !bg-surface-brand-medium-default hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active'
                                )}
                                onClick={() => handleDirectionChange('desc')}
                            >
                                <DescendingIcon />{' '}
                                {selectedOption?.descLabel
                                    ? selectedOption?.descLabel
                                    : selectedOption?.isDate
                                      ? t('overview.filters.orderBy.options.descendingDate')
                                      : t('overview.filters.orderBy.options.descending')}
                            </button>
                        </div>
                        <DropdownMenu.RadioGroup
                            value={selectedOption?.value}
                            onValueChange={(value) => handleValueChange?.(value)}
                            className="p-1"
                        >
                            {options?.map((option) => (
                                <DropdownMenu.RadioItem
                                    key={option.value}
                                    value={option.value}
                                    onSelect={(e) => e.preventDefault()}
                                >
                                    {option.label}
                                </DropdownMenu.RadioItem>
                            ))}
                        </DropdownMenu.RadioGroup>
                    </div>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu>
    );
};

export default OrderByDropdown;
